import {
  ALL_LEAVE_POLICY_TYPES,
  IAttendanceRequestType,
  LeavePolicy,
  LeavePolicyParams,
  LeavePolicyType,
  LeaveRequestsParams,
  ILeaveUseRequest
} from '../entities/leave';
import { Settings } from '../settings';
import { BaseOrganizationEndpoints } from './base';
import { ApiClientError } from './response/ApiClientError';
import { PaginatedResult } from './response/paginated_result';

type LeavePolicyAll = LeavePolicy[];

export class LeaveClient extends BaseOrganizationEndpoints {
  protected formatParams(
    params: { [idx: string]: string | string[] | number } = {}
  ) {
    return this.formatGenericParams(params);
  }
  async createPolicy(payload: Omit<LeavePolicy, 'id'>) {
    return this.requestWithStatus<LeavePolicy>(
      'POST',
      `/work_policies`,
      payload
    );
  }

  async deletePolicy(id: string) {
    return this.requestWithStatus<LeavePolicy>(
      'DELETE',
      `/work_policies/${id}`
    );
  }

  async updatePolicy(payload: LeavePolicy) {
    return this.requestWithStatus<LeavePolicy>(
      'PUT',
      `/work_policies/${payload.id}`,
      payload
    );
  }

  async allPolicies(params: LeavePolicyParams) {
    const urlParams = this.formatParams(params);
    return this.requestWithStatus<PaginatedResult<LeavePolicy>>(
      'GET',
      `/work_policies?${urlParams.toString()}`
    );
  }

  async applicablePolicies(
    workPolicyType: LeavePolicyType,
    options: { start?: string; end?: string; tz: string }
  ) {
    const urlParams = this.formatParams({ workPolicyType, ...options });
    return this.requestWithStatus<{
      policy: LeavePolicy;
      allotted: number | null;
      available: number | null;
      used: number | null;
    }>('GET', `/work_policies/applicable/?${urlParams.toString()}`);
  }

  async allApplicablePolicies(params: {
    start?: string;
    end?: string;
    tz: string;
    type?: LeavePolicyType[];
  }): Promise<
    {
      policy: LeavePolicy;
      allotted: number | null;
      available: number | null;
      used: number | null;
    }[]
  > {
    const askedTypes = params.type ?? ALL_LEAVE_POLICY_TYPES;
    const policiesRes = askedTypes.map(async (type) => {
      try {
        const res = await this.applicablePolicies(type, params);
        return res.result;
      } catch (e) {
        if (e instanceof ApiClientError && e.response.status === 404) {
          return [];
        }
        console.error('Error while fetching applicable policies', e);
        throw e;
      }
    });
    const policies = await Promise.all(policiesRes);
    return policies.flat().filter(x => !!x);
  }

  async createRequest(payload: ILeaveUseRequest) {
    return this.requestWithStatus<LeavePolicy>(
      'POST',
      `/work_policies/uses`,
      payload
    );
  }

  async allRequests(params: LeaveRequestsParams, managed = false) {
    const urlParams = this.formatParams(params);
    return this.requestWithStatus<PaginatedResult<IAttendanceRequestType>>(
      'GET',
      `/work_policies/uses/${managed ? 'managed/' : ''}?${urlParams.toString()}`
    );
  }

  async leaveRequest(id: string) {
    return this.requestWithStatus<IAttendanceRequestType>(
      'GET',
      `${this.settings.adminAuth ? '/integrations' : ''}/work_policies/uses/${id}`
    );
  }

  async respondRequests(payload: {
    ids: string[];
    status: 'accepted' | 'rejected';
    comments?: string | null;
  }) {
    return this.requestWithStatus<LeavePolicy>(
      'PUT',
      `/work_policies/uses/respond`,
      payload
    );
  }

  async deleteRequests(ids: string[]) {
    return Promise.all(
      ids.map((id) =>
        this.requestWithStatus<LeavePolicy>(
          'DELETE',
          `/work_policies/uses/${id}`
        )
      )
    );
  }

  async conflicts(payload: { start: string; end: string; tz?: string }) {
    const urlParams = this.formatParams(payload);
    return this.requestWithStatus<PaginatedResult<IAttendanceRequestType>>(
      'GET',
      `/work_policies/uses/matching?${urlParams}`
    );
  }
}
